/**
 * mandate-registration-view-queries.ts - the TanStack Query hooks of the area «Mandate & Eintragungen»:
 * the customer's view (with its filters), the header tile «Eintragung bei Versicherern», the mirror on
 * the Versicherer and the Nachweis-Dossier export.
 *
 * The keys sit under the 'registrations' prefix, so every registration decision (which invalidates the
 * prefix) refreshes the area, the tile and the mirror.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CarrierRegistrationsDto,
  MandateRegistrationsViewDto,
  RegistrationDossierExportDto,
  RegistrationViewFilters,
} from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';

/** The area of one customer, filtered on the api. */
export function useRegistrationView(
  subjectType: 'person' | 'firma',
  subjectId: string,
  filters: RegistrationViewFilters,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<MandateRegistrationsViewDto>({
    queryKey: [...qk.registrations(subjectType, subjectId), filters],
    queryFn: () => apiClient.registrations.view(subjectType, subjectId, filters),
    enabled: opts.enabled ?? true,
  });
}

/** The header tile (the light read: tiles and mandates, no rows). */
export function useRegistrationTile(subjectType: 'person' | 'firma' | undefined, subjectId: string, opts: { enabled?: boolean } = {}) {
  return useQuery<MandateRegistrationsViewDto>({
    queryKey: qk.registrationTile(subjectType ?? 'firma', subjectId),
    queryFn: () => apiClient.registrations.view(subjectType!, subjectId, { tiles_only: true } as RegistrationViewFilters),
    enabled: (opts.enabled ?? true) && subjectType !== undefined,
  });
}

/** The customers registered at one insurer. */
export function useCarrierRegistrations(carrierUid: string, opts: { enabled?: boolean } = {}) {
  return useQuery<CarrierRegistrationsDto>({
    queryKey: qk.carrierRegistrations(carrierUid),
    queryFn: () => apiClient.carriers.mandateRegistrations(carrierUid),
    enabled: opts.enabled ?? true,
  });
}

/** The Nachweis-Dossier of one insurer (carrier_key) or of the whole customer; the filed document refreshes the Dokumente. */
export function useExportDossier(subjectType: 'person' | 'firma', subjectId: string) {
  const qc = useQueryClient();
  return useMutation<RegistrationDossierExportDto, Error, { carrier_key?: string }>({
    mutationFn: (body) => apiClient.registrations.exportDossier(subjectType, subjectId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: qk.clientDocuments(subjectId) });
    },
  });
}
